import React from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Circle, ExternalLink, Rocket, X } from 'lucide-react';
import { SnapchatGhostIcon } from './SnapchatAccountSection';
import { snapchatAdsManagerUrl } from './snapchatConfig';

const STEPS = [
  ['creative', 'Creative', 'creative_id'],
  ['campaign', 'Campaign', 'campaign_id'],
  ['adsquad', 'Ad Squad', 'ad_squad_id'],
  ['ad', 'Ad', 'ad_id'],
];

export const SnapchatSuccessModal = ({ result, accountId, onClose, onActivate, onCreateAnother, isActivating }) => {
  if (!result) return null;
  const success = result.status === 'success';
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4" data-testid="snapchat-success-modal">
      <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl max-w-lg w-full shadow-2xl border border-white/10 overflow-hidden">
        <div className="p-5 border-b border-white/10 flex items-start justify-between bg-[#FFFC00]/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FFFC00] text-black flex items-center justify-center"><SnapchatGhostIcon className="h-5 w-5" /></div>
            <div>
              <h2 className="text-white font-semibold">{success ? 'Submitted to Snapchat' : result.status === 'partial' ? 'Partially Created' : 'Submission Failed'}</h2>
              <p className="text-xs text-white/60">{success ? 'Your campaign is paused. Activate it to start Snapchat review.' : 'Review the details below.'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white" data-testid="snapchat-success-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-2">
            {STEPS.map(([key, label, idKey]) => {
              const done = result.steps_completed?.includes(key);
              return (
                <div key={key} className={`flex items-center justify-between p-3 rounded-lg border ${done ? 'bg-green-500/10 border-green-500/30' : 'bg-white/5 border-white/10'}`}>
                  <div className="flex items-center gap-2">
                    {done ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Circle className="h-4 w-4 text-white/30" />}
                    <span className="text-sm text-white">{label}</span>
                  </div>
                  {done && result[idKey] && <span className="text-[11px] font-mono text-white/50">{result[idKey]}</span>}
                </div>
              );
            })}
          </div>

          {result.errors?.length > 0 && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 space-y-1" data-testid="snapchat-submit-errors">
              {result.errors.map((err, i) => (
                <p key={i} className="text-xs text-red-300 flex items-start gap-2"><AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {err}</p>
              ))}
            </div>
          )}

          {success && (
            <div className="p-4 rounded-xl bg-gradient-to-r from-yellow-400/10 to-amber-500/10 border border-yellow-400/30 space-y-2">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Rocket className="h-4 w-4 text-yellow-300" /> Ready to go live?</h3>
              <p className="text-xs text-white/60">Activating sets the campaign, ad squad and ad to ACTIVE. Snapchat reviews ads before they start delivering.</p>
              <Button onClick={onActivate} disabled={isActivating} className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white" data-testid="snapchat-success-activate-btn">
                <Rocket className="h-4 w-4 mr-2" /> {isActivating ? 'Activating...' : 'Activate Campaign'}
              </Button>
            </div>
          )}

          {result.campaign_id && accountId && (
            <a href={snapchatAdsManagerUrl(accountId)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-yellow-200 hover:text-white text-sm" data-testid="snapchat-success-ads-manager-link">
              <ExternalLink className="h-4 w-4" /> View in Snapchat Ads Manager
            </a>
          )}
        </div>

        <div className="border-t border-white/10 p-4 flex justify-end gap-3 bg-black/20">
          <Button variant="outline" onClick={onCreateAnother} className="border-white/20 text-white hover:bg-white/10" data-testid="snapchat-success-create-another">Create Another</Button>
          <Button onClick={onClose} className="bg-[#FFFC00] hover:bg-[#f0ed00] text-black font-semibold" data-testid="snapchat-success-done">Done</Button>
        </div>
      </div>
    </div>
  );
};
